import React, { useState } from 'react';
import { Trash2, Globe, FileText, ChevronDown, Link2 } from 'lucide-react';

export function KnowledgeTab({ formData, knowledgeSources, newUrl, setNewUrl, urlLoading, scrapeWithJs, setScrapeWithJs, includeSubpages, setIncludeSubpages, addKnowledgeUrl, foundSubpages, selectedSubpages, setSelectedSubpages, subpageLoading, scrapeSelectedSubpages, setFoundSubpages, uploadLoading, uploadDocument, deleteKnowledgeSource }) {
  const [mode, setMode] = useState('url');

  return (
    <div className="space-y-5">
      {/* Add Source - unified card */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {/* Mode Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setMode('url')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${mode === 'url' ? 'text-indigo-600 bg-indigo-50/60 border-b-2 border-indigo-500' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            data-testid="knowledge-mode-url"
          >
            <Globe className="w-4 h-4" />
            Webseite
          </button>
          <button
            onClick={() => setMode('file')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${mode === 'file' ? 'text-indigo-600 bg-indigo-50/60 border-b-2 border-indigo-500' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            data-testid="knowledge-mode-file"
          >
            <FileText className="w-4 h-4" />
            Datei hochladen
          </button>
        </div>

        {/* URL Mode */}
        {mode === 'url' && (
          <div className="p-4 space-y-3">
            <div className="flex gap-2">
              <input
                type="url"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://ihre-webseite.de"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                disabled={urlLoading}
                onKeyDown={(e) => e.key === 'Enter' && !urlLoading && newUrl.trim() && addKnowledgeUrl()}
                data-testid="knowledge-url-input"
              />
              <button
                onClick={addKnowledgeUrl}
                disabled={urlLoading || !newUrl.trim()}
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors text-sm font-medium flex items-center gap-2 whitespace-nowrap"
                data-testid="knowledge-add-url-btn"
              >
                {urlLoading ? (
                  <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span> Lädt...</>
                ) : (
                  <>Hinzufügen</>
                )}
              </button>
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-gray-500 cursor-pointer hover:text-gray-700 transition-colors">
              <input
                type="checkbox"
                checked={includeSubpages}
                onChange={(e) => setIncludeSubpages(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
              />
              Unterseiten automatisch finden
            </label>
          </div>
        )}

        {/* File Mode */}
        {mode === 'file' && (
          <div className="p-4">
            <label className={`flex flex-col items-center justify-center gap-2 px-6 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-all ${uploadLoading ? 'border-gray-300 bg-gray-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/30'}`}>
              <input type="file" accept=".pdf,.docx,.txt,.md" onChange={uploadDocument} disabled={uploadLoading} className="hidden" data-testid="knowledge-file-input" />
              {uploadLoading ? (
                <>
                  <span className="animate-spin inline-block w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full"></span>
                  <span className="text-sm text-gray-500">Wird verarbeitet...</span>
                </>
              ) : (
                <>
                  <FileText className="w-8 h-8 text-gray-400" />
                  <span className="text-sm text-gray-600 font-medium">Datei auswählen</span>
                  <span className="text-xs text-gray-400">PDF, Word, TXT oder Markdown (max. 10 MB)</span>
                </>
              )}
            </label>
          </div>
        )}
      </div>

      {/* Subpages Found */}
      {foundSubpages.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h4 className="font-medium text-amber-800 mb-3 text-sm">Gefundene Unterseiten ({foundSubpages.length})</h4>
          <div className="space-y-1.5 max-h-40 overflow-y-auto mb-3">
            {foundSubpages.map((subUrl, idx) => (
              <label key={idx} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={selectedSubpages.includes(subUrl)} onChange={(e) => e.target.checked ? setSelectedSubpages([...selectedSubpages, subUrl]) : setSelectedSubpages(selectedSubpages.filter(u => u !== subUrl))} className="rounded border-gray-300 text-amber-600 w-3.5 h-3.5" />
                <span className="truncate text-gray-700 text-xs">{subUrl}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setSelectedSubpages(foundSubpages)} className="text-xs px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors">Alle auswählen</button>
            <button onClick={scrapeSelectedSubpages} disabled={selectedSubpages.length === 0 || subpageLoading} className="text-xs px-4 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center gap-1">
              {subpageLoading ? 'Importiere...' : `${selectedSubpages.length} importieren`}
            </button>
            <button onClick={() => { setFoundSubpages([]); setSelectedSubpages([]); }} className="text-xs px-3 py-1.5 text-gray-500 hover:text-gray-700">Abbrechen</button>
          </div>
        </div>
      )}

      {/* Knowledge Sources List */}
      <div className="space-y-2">
        {knowledgeSources.length > 0 && (
          <h4 className="font-medium text-gray-700 text-sm">Wissensquellen ({knowledgeSources.length})</h4>
        )}
        {knowledgeSources.length === 0 ? (
          <div className="text-center py-10 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Link2 className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500 font-medium">Noch keine Wissensquellen</p>
            <p className="text-xs text-gray-400 mt-1">Webseite oder Datei oben hinzufügen</p>
          </div>
        ) : (
          knowledgeSources.map((source) => (
            <div key={source.id} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3 group hover:border-gray-300 transition-colors">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${source.type === 'url' ? 'bg-blue-50 text-blue-500' : 'bg-purple-50 text-purple-500'}`}>
                {source.type === 'url' ? <Globe className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800 truncate">{source.name}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${source.status === 'ready' ? 'bg-emerald-100 text-emerald-700' : source.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {source.status === 'ready' ? 'Bereit' : source.status === 'error' ? 'Fehler' : 'Lädt'}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {source.type === 'url' && <a href={source.source} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline truncate">{source.source}</a>}
                  {source.error && <span className="text-xs text-red-500">{source.error}</span>}
                  {source.size > 0 && <span className="text-[10px] text-gray-400">{source.size.toLocaleString()} Zeichen</span>}
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); deleteKnowledgeSource(source.id); }}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                title="Löschen"
                data-testid={`delete-source-${source.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
